import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";

const Wrapper = styled.div`
    background-color: white;
    padding: 120px 20px 40px;
    display: flex;
    max-height: 499px;
    flex-direction: column;
    align-items: center;
    min-height: 1vh;

    @media (max-width: 768px) {
        padding: 80px 16px 30px;
        margin-top: 39px;
        max-height: none;
        height: auto;
        margin-left: 25px;
        margin-right: 25px;
    }
`;

const CarouselContainer = styled.div`
    position: relative;
    width: 100%;
    max-width: 1200px;
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const Card = styled.div`
    position: relative;
    background-color: #f7f7f9;
    border-radius: 24px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    text-align: left;
    width: 100%;
    height: auto;
    min-height: 520px;

    @media (max-width: 768px) {
        min-height: auto;
        padding-bottom: 80px;
    }
`;

const MotionCard = motion(Card);

const GifContainer = styled.div`
    display: flex;
    gap: 20px;
    margin-bottom: 15px;
    height: auto;
    flex-direction: row;
    justify-content: center;

    @media (max-width: 768px) {
        flex-direction: column;
        gap: 15px;
    }
`;

const GifWrapper = styled.div`
    position: relative;
    width: 100%;
    height: 300px;
    border-radius: 16px;
    overflow: hidden;

    @media (max-width: 768px) {
        height: 200px;
    }
`;

const GifLabel = styled.div`
    position: absolute;
    top: 8px;
    left: 8px;
    background-color: rgba(0, 0, 0, 0.6);
    color: white;
    padding: 4px 8px;
    border-radius: 8px;
    font-size: 14px;
    z-index: 2;
`;

const mediaStyles = `
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 16px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    display: block;
`;

const ProjectImage = styled.img`
    ${mediaStyles}
`;

const ProjectVideo = styled.video`
    ${mediaStyles}
`;

const ProjectTitle = styled.h3`
    font-size: 24px;
    font-weight: 600;
    margin-top: 20px;
    margin-bottom: 8px;

    @media (max-width: 768px) {
        font-size: 20px;
    }
`;

const ProjectDescription = styled.p`
    font-size: 20px;
    color: #676666;
    margin: 0;
    padding-bottom: 60px;

    @media (max-width: 768px) {
        font-size: 16px;
        padding-bottom: 20px;
    }
`;

const GitHubButton = styled.a`
    position: absolute;
    bottom: 20px;
    right: 20px;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background-color: #24292e;
    color: white;
    text-decoration: none;
    box-shadow: 0 3px 6px rgba(0,0,0,0.2);
    transition: background-color 0.3s ease;
    z-index: 10;

    &:hover {
        background-color: #444c56;
    }

    svg {
        width: 24px;
        height: 24px;
        fill: white;
    }
`;

const ControlsContainer = styled.div`
    display: flex;
    gap: 16px;
    margin-top: 30px;
    justify-content: center;
    z-index: 5;
`;

const ArrowButton = styled.button`
    background-color: white;
    border: 1px solid #ddd;
    border-radius: 50%;
    width: 35px;
    height: 35px;
    display: flex;
    margin-top: -80px;
    margin-bottom: -1000px;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);

    &:hover {
        background-color: #24292e;
        border-color: #24292e;
        transform: scale(1.1);
        svg {
            fill: white;
        }
    }

    svg {
        width: 18px;
        height: 18px;
        fill: #333;
        transition: fill 0.2s ease;
    }

    @media (max-width: 768px) {
        margin-top: -82px;
        margin-bottom: 0px;
    }
`;

const ProjectsPage = () => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isAutoPlaying, setIsAutoPlaying] = useState(true);

    const projects = [
        {
            labels: ["Admin's View", "User's View"],
            title: "Card Management System",
            description: "The Card Management System is a web application that allows users to transfer money, search and view details about their collection of cards. Registered users can manage their own cards, while admins have access to add, update balance, block or delete cards from the system. The platform features a clean and responsive design, data encryption, user authentication, and role-based access control.",
            media: ["/pr13.mp4", "/pr25.mp4"],
            githubUrl: "https://github.com/eminjafarli/bankrest"
        },
        {
            labels: ["Real Example", "Menu"],
            title: "Scaling System",
            description: "The Scaling System is a system developed for an agricultural company to manage and complete purchases directly at the factory. The application integrates hardware scales with a digital system to automate the weighing process, record transactions, and generate purchase receipts.",
            media: ["/IMG_7045.mp4", "/IMG_7047.JPG"],
            githubUrl: "https://github.com/eminjafarli/Scales"
        }
    ];

    useEffect(() => {
        if (!isAutoPlaying) return;
        const interval = setInterval(() => {
            setCurrentIndex((prevIndex) => (prevIndex + 1) % projects.length);
        }, 10000);
        return () => clearInterval(interval);
    }, [currentIndex, isAutoPlaying, projects.length]);

    const handleManualNext = () => {
        setIsAutoPlaying(false);
        setCurrentIndex((prevIndex) => (prevIndex + 1) % projects.length);
    };

    const handleManualPrev = () => {
        setIsAutoPlaying(false);
        setCurrentIndex((prevIndex) =>
            prevIndex === 0 ? projects.length - 1 : prevIndex - 1
        );
    };

    const renderMediaItem = (src, altText) => {
        const isVideo = src.toLowerCase().endsWith(".mp4") || src.toLowerCase().endsWith(".webm");

        if (isVideo) {
            return (
                <ProjectVideo
                    autoPlay
                    loop
                    muted
                    playsInline
                    src={src}
                />
            );
        }
        return <ProjectImage src={src} alt={altText} />;
    };

    const currentProject = projects[currentIndex];

    return (
        <Wrapper>
            <CarouselContainer>
                <AnimatePresence mode="wait">
                    <MotionCard
                        key={currentIndex}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.3 }}
                    >
                        <GifContainer>
                            <GifWrapper>
                                <GifLabel>{currentProject.labels[0]}</GifLabel>
                                {renderMediaItem(currentProject.media[0], "View 1")}
                            </GifWrapper>
                            <GifWrapper>
                                <GifLabel>{currentProject.labels[1]}</GifLabel>
                                {renderMediaItem(currentProject.media[1], "View 2")}
                            </GifWrapper>
                        </GifContainer>
                        <ProjectTitle>{currentProject.title}</ProjectTitle>
                        <ProjectDescription>{currentProject.description}</ProjectDescription>

                        <GitHubButton
                            href={currentProject.githubUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={`View ${currentProject.title} on GitHub`}
                        >
                            <svg viewBox="0 0 24 24" aria-hidden="true">
                                <path fillRule="evenodd" clipRule="evenodd" d="M12 0C5.372 0 0 5.373 0 12c0 5.303 3.438 9.8 8.205 11.387.6.112.82-.26.82-.577v-2.234c-3.338.726-4.033-1.415-4.033-1.415-.546-1.386-1.333-1.755-1.333-1.755-1.09-.745.083-.73.083-.73 1.205.085 1.838 1.238 1.838 1.238 1.07 1.835 2.807 1.305 3.492.997.108-.775.418-1.305.76-1.605-2.665-.3-5.466-1.335-5.466-5.93 0-1.31.47-2.38 1.237-3.22-.124-.303-.535-1.523.117-3.176 0 0 1.008-.322 3.3 1.23a11.46 11.46 0 013.003-.403c1.02.004 2.047.138 3.003.403 2.29-1.552 3.296-1.23 3.296-1.23.655 1.653.244 2.873.12 3.176.77.84 1.235 1.91 1.235 3.22 0 4.61-2.807 5.625-5.48 5.922.43.372.81 1.103.81 2.222v3.293c0 .319.217.694.825.576C20.565 21.796 24 17.3 24 12c0-6.627-5.373-12-12-12z" />
                            </svg>
                        </GitHubButton>
                    </MotionCard>
                </AnimatePresence>

                <ControlsContainer>
                    <ArrowButton onClick={handleManualPrev} aria-label="Previous Project">
                        <svg viewBox="0 0 24 24">
                            <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
                        </svg>
                    </ArrowButton>

                    <ArrowButton onClick={handleManualNext} aria-label="Next Project">
                        <svg viewBox="0 0 24 24">
                            <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
                        </svg>
                    </ArrowButton>
                </ControlsContainer>
            </CarouselContainer>
        </Wrapper>
    );
};

export default ProjectsPage;